/*###########################################################################
// Hero home
//#########################################################################*/

import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import "../vendor/_splitText"
import { throttle, debounce } from "../utilities/_throttleDebounce"
gsap.registerPlugin(ScrollTrigger)

const heroHome = {
	config: {
		heroHome: '.heroHome',
		heroHomeMedia: '.heroHome__media',
		heroHomeMediaSound: '.heroHome__media__controls__sound',
		heroHomeHeading: '.heroHome__heading',
		heroHomeContent: '.heroHome__content'
	},

	elements: {},

	state: {
		isDesktop: false,
		muted: true,
		tlEndValue: 0,
		afterFinishHoldDuration: 0.5,
	},

	tl: null,
	tlOnScroll: null,

	init() {
		const self = this

		self.elements.heroHome = document.querySelector(self.config.heroHome)

		if (self.elements.heroHome !== null) {
			self.elements.heroHome.style.opacity = '1'
			self.elements.heroHomeHeading = self.elements.heroHome.querySelector(self.config.heroHomeHeading)
			self.elements.heroHomeMedia = self.elements.heroHome.querySelector(self.config.heroHomeMedia)
			self.elements.heroHomeMediaSound = self.elements.heroHome.querySelector(self.config.heroHomeMediaSound)
			self.elements.heroHomeMediaSoundText = self.elements.heroHome.querySelector('.heroHome__media__controls__sound__text')
			self.elements.heroHomeContent = self.elements.heroHome.querySelector(self.config.heroHomeContent)

			// Handle hero main
			//
			setTimeout(() => {
				self.elements.heroHome.style.height = (window.innerHeight - document.querySelector('.header').clientHeight) + 'px'
			}, 250)

			setTimeout(() => {
				self.elements.heroHome.style.height = (window.innerHeight - document.querySelector('.header').clientHeight) + 'px'
			}, 2000)

			self.handleHeroMain()

			if (window.innerWidth >= 1024) {
				self.state.isDesktop = true
				self.state.tlEndValue = self.elements.heroHome.clientHeight * 2
				self.state.afterFinishHoldDuration = 0.25
			} else {
				self.state.isDesktop = false
				self.state.tlEndValue = self.elements.heroHome.clientHeight * 1.25
				self.state.afterFinishHoldDuration = 0.05
			}

			let width = window.innerWidth
			window.addEventListener('resize', debounce(function() {
				if (width != window.innerWidth) {
					self.elements.heroHome.style.height = (window.innerHeight - document.querySelector('.header').clientHeight) + 'px'

					width = window.innerWidth
				}

				if (window.innerWidth >= 1024) {
					if (!self.state.isDesktop) {
						self.state.isDesktop = true
						self.state.tlEndValue = self.elements.heroHome.clientHeight * 2
						self.state.afterFinishHoldDuration = 0.25
					}
				} else {
					if (self.state.isDesktop) {
						self.state.isDesktop = false
						self.state.tlEndValue = self.elements.heroHome.clientHeight * 1.25
						self.state.afterFinishHoldDuration = 0.05
					}
				}
			}, 250))

			// Handle sound toggle
			//
			self.handleSoundToggle()

			// Set loaded class
			//
			setTimeout(() => {
				self.elements.heroHome.classList.add('has-loaded')
			}, 250)
		}
	},

	handleHeroMain() {
		const self = this

		const handleViewportChange = debounce(function() {
			ScrollTrigger.refresh()
		}, 250)

		window.addEventListener('resize', handleViewportChange)
		window.addEventListener('orientationchange', handleViewportChange)

		let split = new SplitText(self.elements.heroHomeHeading, {
      type: "lines",
    })

		self.tl = gsap.timeline({
			scrollTrigger: {
				trigger: self.elements.heroHome.parentNode,
				start: "top top",
				end: () => "+=" + self.state.tlEndValue,
				pin: true,
				pinSpacing: true,
				scrub: 0.85,
				invalidateOnRefresh: true,
				onUpdate: throttle((st) => {
					if (st.progress > 0.25 && st.direction === 1) {
						self.elements.heroHomeMedia.querySelector('video').play()
					} else if (st.progress > 0.1 && st.progress < 0.25 && st.direction === -1) {
						self.elements.heroHomeMedia.querySelector('video').pause()
					} else if (st.progress <= 0.1 && st.direction === -1) {
						self.elements.heroHomeMedia.querySelector('video').currentTime = 0
					}
				}, 250)
			},
		})

		self.tlOnScroll = gsap.timeline({
			scrollTrigger: {
				trigger: self.elements.heroHome.parentNode,
				start: "bottom center",
				end: "bottom top",
				onEnter: (st) => {
					if (!self.state.muted) {
						self.toggleSound()
					}
				},
				onLeave: (st) => {
					self.elements.heroHomeMedia.querySelector('video').pause()
					self.elements.heroHomeMedia.querySelector('video').currentTime = 0
				},
				onLeaveBack: (st) => {
					self.elements.heroHomeMedia.querySelector('video').play()
				}
			},
		})

		self.tl.scrollTrigger.disable()

		self.tl.fromTo(self.elements.heroHomeMedia, {
			scale: 0,
			opacity: 0,
			borderRadius: 0,
		}, {
			duration: 2,
			scale: 1,
			opacity: 1,
			borderRadius: self.state.isDesktop ? 24 : 16,
			ease: "power1.out"
		})

    self.tl.to(split.lines, {
      delay: 0.25,
      duration: 1.25,
      opacity: 0,
			scale: 0.95,
			y: self.state.isDesktop ? 80 : 40,
      ease: "power1.out",
      stagger: {
				each: 0.2,
				from: "end"
			}
    }, "-=2.0")

		self.tl.to({}, { duration: self.state.afterFinishHoldDuration })

		// Scroll to top of page on load to ensure hero animation plays correctly and pinned scrolling is not skipped
		//
		requestAnimationFrame(() => {
			window.scrollTo(0, 0)
		})

		gsap.fromTo(split.lines, {
      opacity: 0,
			y: self.state.isDesktop ? 80 : 40,
    }, {
      delay: 1.5,
      duration: 1,
      opacity: 1,
			y: 0,
      ease: "power1.out",
      stagger: 0.1,
			onComplete: () => {
				window.lenis.start()
				self.tl.scrollTrigger.enable()
			}
    }, "-=2.0")

		setTimeout(function() {
			ScrollTrigger.sort()
			ScrollTrigger.refresh()

			window.dispatchEvent(new Event('heroHomeInitialised'))
		}, 1000)
	},

	handleSoundToggle() {
		const self = this

		self.elements.heroHomeMediaSound.addEventListener('click', function() {
			self.toggleSound()
		})
	},

	toggleSound() {
		const self = this

		if (self.state.muted) {
			self.elements.heroHomeMedia.classList.add('heroHome__media--soundOn')
			self.elements.heroHomeMedia.querySelector('video').muted = false
			self.elements.heroHomeMediaSoundText.textContent = self.elements.heroHomeMediaSoundText.dataset.activeText
			self.state.muted = false
		} else {
			self.elements.heroHomeMedia.classList.remove('heroHome__media--soundOn')
			self.elements.heroHomeMedia.querySelector('video').muted = true
			self.elements.heroHomeMediaSoundText.textContent = self.elements.heroHomeMediaSoundText.dataset.inactiveText
			self.state.muted = true
		}
	}
}

initComponent((pageChange) => {
	if (pageChange) {
		if (heroHome.tl !== null) {
			heroHome.tl.kill()
		}

		setTimeout(() => {
			heroHome.init(pageChange)
		}, 250)
	} else {
		heroHome.init(pageChange)
	}
})
